import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { BUSINESS } from "@/data/business";

const NAV_LINKS = [
  { label: "Home", path: "/", testid: "nav-home-link" },
  {
    label: "Deep Tissue",
    path: "/services/deep-tissue-massage",
    testid: "nav-deep-tissue-link",
  },
  {
    label: "Thai Massage",
    path: "/services/thai-massage",
    testid: "nav-thai-massage-link",
  },
  {
    label: "Full Body",
    path: "/services/full-body-massage",
    testid: "nav-full-body-link",
  },
  {
    label: "Aromatherapy",
    path: "/services/aromatherapy-massage",
    testid: "nav-aromatherapy-link",
  },
  { label: "Contact", path: "/contact", testid: "nav-contact-link" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-black/80 backdrop-blur-xl border-b border-ss-gold/15 shadow-[0_4px_30px_rgba(0,0,0,0.4)]"
          : "bg-gradient-to-b from-black/60 to-transparent border-b border-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((link) => (
            <NavLink
              key={link.path}
              to={link.path}
              data-testid={link.testid}
              className={({ isActive }) =>
                `relative text-sm font-medium tracking-wide transition-colors duration-300 ${
                  isActive
                    ? "text-ss-gold"
                    : "text-ss-text/80 hover:text-ss-gold"
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>

        <a
          href={`tel:${BUSINESS.phoneClean}`}
          data-testid="navbar-call-button"
          className="hidden lg:flex items-center gap-2 bg-ss-gold text-ss-bg px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-ss-goldHover transition-all duration-300 hover:gap-3"
        >
          <Phone className="w-3.5 h-3.5" /> Call Now
        </a>

        <button
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          className="lg:hidden text-ss-text p-2"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div
          className="lg:hidden bg-ss-bg border-t border-ss-gold/15 px-5 pb-6 pt-2 flex flex-col gap-4"
          data-testid="mobile-menu-panel"
        >
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setOpen(false)}
              data-testid={`mobile-${link.testid}`}
              className="text-ss-text font-medium py-2 border-b border-ss-gold/10"
            >
              {link.label}
            </Link>
          ))}
          <a
            href={`tel:${BUSINESS.phoneClean}`}
            data-testid="mobile-call-button"
            className="flex items-center justify-center gap-2 bg-ss-gold text-ss-bg px-5 py-3 rounded-full text-sm font-semibold mt-2"
          >
            <Phone className="w-4 h-4" /> Call {BUSINESS.phoneDisplay}
          </a>
        </div>
      )}
    </header>
  );
};
